import React, { useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEnvelope, faComment, faToggleOn, faToggleOff, faPrint, faCheck, faLayerGroup, faCircleCheck } from '@fortawesome/free-solid-svg-icons';
import { UI_COLORS, colorHelpers } from '../utils/colors.jsx';
import { useChannelSettings } from '../context/ChannelSettingsContext';
import { usePrintSettings } from '../context/PrintSettingsContext';
import { useCoilSettings } from '../context/CoilSettingsContext';
import { useOrderSettings } from '../context/OrderSettingsContext';
import { PAPER_SIZES, PRINT_DOCUMENTS } from '../utils/challanPrintLayout';
import { useRoleAccess } from '../hooks/useRoleAccess';
import { USER_ROLES } from '../utils/constants';
import { extractApiError } from '../utils/apiError';
import { useMessageAlert } from '../components/common/MessageAlert';
import { useTranslation } from 'react-i18next';

const ChannelSettingsPage = () => {
  const { t } = useTranslation();
  const CH = t('channelSettings', { returnObjects: true });
  const PS = t('printSettings', { returnObjects: true });
  const CS = t('coilSettings', { returnObjects: true });
  const OS = t('orderSettings', { returnObjects: true });
  const { emailEnabled, whatsappEnabled, loading, updateChannelSetting } = useChannelSettings();
  const { userRole } = useRoleAccess();
  const { showSuccess, showError } = useMessageAlert();
  const [saving, setSaving] = useState(null);
  const {
    paperSizes,
    loading: printLoading,
    updatePrintSetting,
  } = usePrintSettings();
  const [savingPrint, setSavingPrint] = useState(null);
  const {
    coilTrackingEnabled,
    loading: coilLoading,
    updateCoilSetting,
  } = useCoilSettings();
  const [savingCoil, setSavingCoil] = useState(false);
  const {
    skipOrderConfirmation,
    loading: orderLoading,
    updateSkipOrderConfirmation,
  } = useOrderSettings();
  const [savingOrder, setSavingOrder] = useState(false);

  const isSuperAdmin = userRole === USER_ROLES.SUPER_ADMIN;

  const handleToggle = async (channel, currentValue) => {
    if (!isSuperAdmin) return;
    setSaving(channel);
    try {
      await updateChannelSetting(channel, !currentValue);
      const channelLabel = channel === 'email' ? CH.channels.email.label : CH.channels.whatsapp.label;
      const state = !currentValue ? CH.status.enabled.toLowerCase() : CH.status.disabled.toLowerCase();
      showSuccess(t('channelSettings.notifications.updated', { channel: channelLabel, state }));
    } catch (err) {
      const channelLabel = channel === 'email' ? CH.channels.email.label : CH.channels.whatsapp.label;
      showError(extractApiError(err) || t('channelSettings.notifications.updateFailed', { channel: channelLabel }));
    } finally {
      setSaving(null);
    }
  };

  const handlePaperSizeChange = async (documentType, paperSize) => {
    if (!isSuperAdmin) return;
    setSavingPrint(documentType);
    try {
      await updatePrintSetting(documentType, paperSize);
      showSuccess(
        PS.saved.replace('{{document}}', PS.documents[documentType].label).replace('{{size}}', paperSize)
      );
    } catch (err) {
      showError(PS.saveFailed.replace('{{document}}', PS.documents[documentType].label));
    } finally {
      setSavingPrint(null);
    }
  };

  const handleCoilToggle = async () => {
    if (!isSuperAdmin) return;
    setSavingCoil(true);
    try {
      await updateCoilSetting(!coilTrackingEnabled);
      const state = !coilTrackingEnabled ? CS.status.enabled.toLowerCase() : CS.status.disabled.toLowerCase();
      showSuccess(t('coilSettings.notifications.updated', { state }));
    } catch (err) {
      showError(extractApiError(err) || t('coilSettings.notifications.updateFailed'));
    } finally {
      setSavingCoil(false);
    }
  };

  const handleSkipConfirmationToggle = async () => {
    if (!isSuperAdmin) return;
    setSavingOrder(true);
    try {
      const next = !skipOrderConfirmation;
      await updateSkipOrderConfirmation(next);
      showSuccess(next ? OS.notifications.enabled : OS.notifications.disabled);
    } catch (err) {
      showError(extractApiError(err) || OS.notifications.updateFailed);
    } finally {
      setSavingOrder(false);
    }
  };

  // Plain size names — which one is in use is shown by the selected state and the
  // "current" line under the document, so the label itself must not claim it.
  const paperOptions = [
    { value: PAPER_SIZES.A5, label: PS.sizes.a5 },
    { value: PAPER_SIZES.A4, label: PS.sizes.a4 },
  ];

  // Each challan layout is fixed to one orientation; surfaced so the person picking
  // a paper size knows which way the sheet comes out.
  const printableDocuments = [
    { type: PRINT_DOCUMENTS.DELIVERY_CHALLAN, orientation: 'portrait' },
    { type: PRINT_DOCUMENTS.PURCHASE_CHALLAN, orientation: 'landscape' },
  ];

  const channels = [
    {
      key: 'email',
      label: CH.channels.email.label,
      description: CH.channels.email.description,
      icon: faEnvelope,
      enabled: emailEnabled,
      iconColor: UI_COLORS.STATUS.INFO,
    },
    {
      key: 'whatsapp',
      label: CH.channels.whatsapp.label,
      description: CH.channels.whatsapp.description,
      icon: faComment,
      iconColor: '#25D366',
      enabled: whatsappEnabled,
    },
  ];

  return (
    <div className="p-6 max-w-3xl">
      <div className="mb-6">
        <h1 className="text-2xl font-bold" style={{ color: UI_COLORS.TEXT.PRIMARY }}>
          {CH.pageTitle}
        </h1>
        <p className="mt-1 text-sm" style={{ color: UI_COLORS.TEXT.MUTED }}>
          {CH.pageSubtitle}
        </p>
        {!isSuperAdmin && (
          <div
            className="mt-3 px-4 py-2 rounded-lg text-sm"
            style={{ backgroundColor: colorHelpers.withOpacity(UI_COLORS.STATUS.WARNING, 0.1), color: UI_COLORS.STATUS.WARNING }}
          >
            {CH.superAdminOnlyBanner}
          </div>
        )}
      </div>

      <h2 className="text-lg font-bold mb-1" style={{ color: UI_COLORS.TEXT.PRIMARY }}>
        {CH.title}
      </h2>
      <p className="mb-4 text-sm" style={{ color: UI_COLORS.TEXT.MUTED }}>
        {CH.subtitle}
      </p>

      {loading ? (
        <div className="space-y-4">
          {[1, 2].map((i) => (
            <div key={i} className="h-20 rounded-xl bg-gray-100 animate-pulse" />
          ))}
        </div>
      ) : (
        <div className="space-y-4">
          {channels.map(({ key, label, description, icon, iconColor, enabled }) => (
            <div
              key={key}
              className="flex items-center justify-between gap-4 p-5 rounded-xl border"
              style={{
                backgroundColor: colorHelpers.withOpacity(UI_COLORS.BACKGROUNDS.SECTION, 0.5),
                borderColor: enabled
                  ? colorHelpers.withOpacity(iconColor, 0.3)
                  : colorHelpers.withOpacity(UI_COLORS.TEXT.MUTED, 0.2),
              }}
            >
              <div className="flex items-center gap-4 min-w-0 flex-1">
                <div
                  className="w-10 h-10 shrink-0 rounded-lg flex items-center justify-center"
                  style={{ backgroundColor: colorHelpers.withOpacity(iconColor, 0.12) }}
                >
                  <FontAwesomeIcon icon={icon} style={{ color: iconColor }} />
                </div>
                <div className="min-w-0">
                  <div className="font-semibold" style={{ color: UI_COLORS.TEXT.PRIMARY }}>{label}</div>
                  <div className="text-xs mt-0.5" style={{ color: UI_COLORS.TEXT.MUTED }}>{description}</div>
                </div>
              </div>

              <button
                onClick={() => handleToggle(key, enabled)}
                disabled={!isSuperAdmin || saving === key}
                className="shrink-0 flex items-center justify-center gap-2 w-32 px-4 py-2 rounded-lg font-medium text-sm transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                style={{
                  backgroundColor: enabled
                    ? colorHelpers.withOpacity(UI_COLORS.STATUS.SUCCESS, 0.1)
                    : colorHelpers.withOpacity(UI_COLORS.TEXT.MUTED, 0.08),
                  color: enabled ? UI_COLORS.STATUS.SUCCESS : UI_COLORS.TEXT.MUTED,
                  border: `1px solid ${enabled ? colorHelpers.withOpacity(UI_COLORS.STATUS.SUCCESS, 0.3) : colorHelpers.withOpacity(UI_COLORS.TEXT.MUTED, 0.2)}`,
                }}
              >
                <FontAwesomeIcon icon={enabled ? faToggleOn : faToggleOff} className="text-lg" />
                {saving === key ? t('common.actions.saving') : enabled ? CH.status.enabled : CH.status.disabled}
              </button>
            </div>
          ))}
        </div>
      )}

      {/* Paper size per printed document. Super-admin only, same as the toggles above. */}
      <div className="mt-10">
        <h2 className="text-lg font-bold" style={{ color: UI_COLORS.TEXT.PRIMARY }}>
          {PS.title}
        </h2>
        <p className="mt-1 mb-4 text-sm" style={{ color: UI_COLORS.TEXT.MUTED }}>
          {PS.subtitle}
        </p>

        {printLoading ? (
          <div className="space-y-4">
            {[1, 2].map((i) => (
              <div key={i} className="h-20 rounded-xl bg-gray-100 animate-pulse" />
            ))}
          </div>
        ) : (
          <div className="space-y-4">
            {printableDocuments.map(({ type: docType, orientation }) => {
              const current = paperSizes[docType];
              const meta = PS.documents[docType];
              const currentLabel = current === PAPER_SIZES.A4 ? PS.sizes.a4 : PS.sizes.a5;
              return (
                <div
                  key={docType}
                  className="flex items-center justify-between gap-4 p-5 rounded-xl border"
                  style={{
                    backgroundColor: colorHelpers.withOpacity(UI_COLORS.BACKGROUNDS.SECTION, 0.5),
                    borderColor: colorHelpers.withOpacity(UI_COLORS.TEXT.MUTED, 0.2),
                  }}
                >
                  <div className="flex items-center gap-4 min-w-0 flex-1">
                    <div
                      className="w-10 h-10 shrink-0 rounded-lg flex items-center justify-center"
                      style={{ backgroundColor: colorHelpers.withOpacity(UI_COLORS.STATUS.INFO, 0.12) }}
                    >
                      <FontAwesomeIcon icon={faPrint} style={{ color: UI_COLORS.STATUS.INFO }} />
                    </div>
                    <div className="min-w-0">
                      <div className="font-semibold" style={{ color: UI_COLORS.TEXT.PRIMARY }}>{meta.label}</div>
                      <div className="text-xs mt-0.5" style={{ color: UI_COLORS.TEXT.MUTED }}>{meta.description}</div>
                      <div className="flex flex-wrap items-center gap-2 mt-1.5">
                        <span
                          className="px-2 py-0.5 rounded text-[11px] font-medium"
                          style={{
                            backgroundColor: colorHelpers.withOpacity(UI_COLORS.STATUS.INFO, 0.1),
                            color: UI_COLORS.STATUS.INFO,
                          }}
                        >
                          {PS.orientation[orientation]}
                        </span>
                        <span className="text-[11px]" style={{ color: UI_COLORS.TEXT.MUTED }}>
                          {PS.currentBadge}: {currentLabel}
                        </span>
                      </div>
                    </div>
                  </div>

                  <div
                    className="shrink-0 flex items-center gap-1 p-1 rounded-lg"
                    style={{ backgroundColor: colorHelpers.withOpacity(UI_COLORS.TEXT.MUTED, 0.08) }}
                  >
                    {paperOptions.map(({ value, label }) => {
                      const active = current === value;
                      return (
                        <button
                          key={value}
                          onClick={() => handlePaperSizeChange(docType, value)}
                          disabled={!isSuperAdmin || savingPrint === docType || active}
                          className="flex items-center justify-center gap-1.5 w-24 px-3 py-1.5 rounded-md font-medium text-sm transition-all disabled:cursor-not-allowed"
                          style={{
                            backgroundColor: active
                              ? colorHelpers.withOpacity(UI_COLORS.STATUS.SUCCESS, 0.12)
                              : 'transparent',
                            color: active ? UI_COLORS.STATUS.SUCCESS : UI_COLORS.TEXT.MUTED,
                            border: `1px solid ${active
                              ? colorHelpers.withOpacity(UI_COLORS.STATUS.SUCCESS, 0.35)
                              : 'transparent'}`,
                            opacity: !isSuperAdmin && !active ? 0.5 : 1,
                          }}
                        >
                          {active && <FontAwesomeIcon icon={faCheck} className="text-xs" />}
                          {savingPrint === docType && !active ? t('common.actions.saving') : label}
                        </button>
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Coil tracking. The master switch for the whole per-coil inventory feature —
          with it off nothing coil-related renders anywhere in the app. Super-admin
          only, same as everything else on this page. */}
      <div className="mt-10">
        <h2 className="text-lg font-bold" style={{ color: UI_COLORS.TEXT.PRIMARY }}>
          {CS.title}
        </h2>
        <p className="mt-1 mb-4 text-sm" style={{ color: UI_COLORS.TEXT.MUTED }}>
          {CS.subtitle}
        </p>

        {coilLoading ? (
          <div className="space-y-4">
            <div className="h-20 rounded-xl bg-gray-100 animate-pulse" />
          </div>
        ) : (
          <div className="space-y-4">
            <div
              className="flex items-center justify-between gap-4 p-5 rounded-xl border"
              style={{
                backgroundColor: colorHelpers.withOpacity(UI_COLORS.BACKGROUNDS.SECTION, 0.5),
                borderColor: coilTrackingEnabled
                  ? colorHelpers.withOpacity(UI_COLORS.BRAND.TERTIARY, 0.3)
                  : colorHelpers.withOpacity(UI_COLORS.TEXT.MUTED, 0.2),
              }}
            >
              <div className="flex items-center gap-4 min-w-0 flex-1">
                <div
                  className="w-10 h-10 shrink-0 rounded-lg flex items-center justify-center"
                  style={{ backgroundColor: colorHelpers.withOpacity(UI_COLORS.BRAND.TERTIARY, 0.12) }}
                >
                  <FontAwesomeIcon icon={faLayerGroup} style={{ color: UI_COLORS.BRAND.TERTIARY }} />
                </div>
                <div className="min-w-0">
                  <div className="font-semibold" style={{ color: UI_COLORS.TEXT.PRIMARY }}>{CS.tracking.label}</div>
                  <div className="text-xs mt-0.5" style={{ color: UI_COLORS.TEXT.MUTED }}>{CS.tracking.description}</div>
                </div>
              </div>

              <button
                onClick={handleCoilToggle}
                disabled={!isSuperAdmin || savingCoil}
                className="shrink-0 flex items-center justify-center gap-2 w-32 px-4 py-2 rounded-lg font-medium text-sm transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                style={{
                  backgroundColor: coilTrackingEnabled
                    ? colorHelpers.withOpacity(UI_COLORS.STATUS.SUCCESS, 0.1)
                    : colorHelpers.withOpacity(UI_COLORS.TEXT.MUTED, 0.08),
                  color: coilTrackingEnabled ? UI_COLORS.STATUS.SUCCESS : UI_COLORS.TEXT.MUTED,
                  border: `1px solid ${coilTrackingEnabled ? colorHelpers.withOpacity(UI_COLORS.STATUS.SUCCESS, 0.3) : colorHelpers.withOpacity(UI_COLORS.TEXT.MUTED, 0.2)}`,
                }}
              >
                <FontAwesomeIcon icon={coilTrackingEnabled ? faToggleOn : faToggleOff} className="text-lg" />
                {savingCoil ? t('common.actions.saving') : coilTrackingEnabled ? CS.status.enabled : CS.status.disabled}
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Skip order confirmation. Covers sales orders and purchase orders with one
          switch, deliberately: the two flows are the same step to the people using
          them, and letting them disagree would be a support question every time.
          Super-admin only, same as everything else on this page. */}
      <div className="mt-10">
        <h2 className="text-lg font-bold" style={{ color: UI_COLORS.TEXT.PRIMARY }}>
          {OS.title}
        </h2>
        <p className="mt-1 mb-4 text-sm" style={{ color: UI_COLORS.TEXT.MUTED }}>
          {OS.subtitle}
        </p>

        {orderLoading ? (
          <div className="space-y-4">
            <div className="h-20 rounded-xl bg-gray-100 animate-pulse" />
          </div>
        ) : (
          <div className="space-y-4">
            <div
              className="flex items-center justify-between gap-4 p-5 rounded-xl border"
              style={{
                backgroundColor: colorHelpers.withOpacity(UI_COLORS.BACKGROUNDS.SECTION, 0.5),
                borderColor: skipOrderConfirmation
                  ? colorHelpers.withOpacity(UI_COLORS.BRAND.TERTIARY, 0.3)
                  : colorHelpers.withOpacity(UI_COLORS.TEXT.MUTED, 0.2),
              }}
            >
              <div className="flex items-center gap-4 min-w-0 flex-1">
                <div
                  className="w-10 h-10 shrink-0 rounded-lg flex items-center justify-center"
                  style={{ backgroundColor: colorHelpers.withOpacity(UI_COLORS.BRAND.TERTIARY, 0.12) }}
                >
                  <FontAwesomeIcon icon={faCircleCheck} style={{ color: UI_COLORS.BRAND.TERTIARY }} />
                </div>
                <div className="min-w-0">
                  <div className="font-semibold" style={{ color: UI_COLORS.TEXT.PRIMARY }}>{OS.skip.label}</div>
                  <div className="text-xs mt-0.5" style={{ color: UI_COLORS.TEXT.MUTED }}>{OS.skip.description}</div>
                </div>
              </div>

              <button
                onClick={handleSkipConfirmationToggle}
                disabled={!isSuperAdmin || savingOrder}
                className="shrink-0 flex items-center justify-center gap-2 w-32 px-4 py-2 rounded-lg font-medium text-sm transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                style={{
                  backgroundColor: skipOrderConfirmation
                    ? colorHelpers.withOpacity(UI_COLORS.STATUS.SUCCESS, 0.1)
                    : colorHelpers.withOpacity(UI_COLORS.TEXT.MUTED, 0.08),
                  color: skipOrderConfirmation ? UI_COLORS.STATUS.SUCCESS : UI_COLORS.TEXT.MUTED,
                  border: `1px solid ${skipOrderConfirmation ? colorHelpers.withOpacity(UI_COLORS.STATUS.SUCCESS, 0.3) : colorHelpers.withOpacity(UI_COLORS.TEXT.MUTED, 0.2)}`,
                }}
              >
                <FontAwesomeIcon icon={skipOrderConfirmation ? faToggleOn : faToggleOff} className="text-lg" />
                {savingOrder ? t('common.actions.saving') : skipOrderConfirmation ? OS.status.enabled : OS.status.disabled}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ChannelSettingsPage;
